import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

export function TrendSection({
  title,
  description,
  children,
  className,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('min-w-0', className)}>
      <div className="mb-3">
        <h4 className="m-0 font-serif text-[0.9375rem] font-semibold text-[var(--inv-text)]">
          {title}
        </h4>
        {description ? (
          <p className="m-0 mt-0.5 max-w-[42rem] text-[0.8125rem] leading-relaxed text-[var(--inv-text-secondary)]">
            {description}
          </p>
        ) : null}
      </div>
      {children}
    </section>
  );
}

export function TrendSegmentGroup<T extends string>({
  label,
  ariaLabel,
  options,
  value,
  onChange,
}: {
  label: string;
  ariaLabel?: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (next: T) => void;
}) {
  return (
    <div className="min-w-0">
      <p className="m-0 mb-1.5 text-[0.625rem] font-semibold uppercase tracking-[0.1em] text-[var(--inv-text-muted)]">
        {label}
      </p>
      <div
        className="inline-flex max-w-full flex-wrap rounded-[10px] border border-solid border-[var(--inv-border-soft)] bg-[var(--inv-surface-muted)] p-0.5"
        role="group"
        aria-label={ariaLabel ?? label}>
        {options.map(opt => {
          const active = opt.value === value;
          return (
            <Button
              key={opt.value}
              type="button"
              variant="ghost"
              className={cn(
                'h-auto rounded-lg px-3 py-1.5 text-[0.8125rem] shadow-none hover:!bg-[var(--inv-surface)]',
                active
                  ? 'bg-[var(--inv-surface)] text-[var(--inv-accent)] shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.06))] hover:!text-[var(--inv-accent)]'
                  : 'bg-transparent text-[var(--inv-text-secondary)] hover:!text-[var(--inv-text)]',
              )}
              onClick={() => onChange(opt.value)}>
              {opt.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
}

export const trendMetricsPanelClass =
  'rounded-xl border border-[var(--inv-border-soft)] bg-[var(--inv-surface-muted)]/60 p-3 sm:p-3.5';

export const trendChartPanelClass =
  'rounded-xl border border-[var(--inv-border)] bg-[var(--inv-surface)] p-3 sm:p-4';
